<!-- Copyright by the Spark Development Network; Licensed under the Rock Community License -->
<template>
    <NotificationBox v-if="config.errorMessage"
                     alertType="warning">
        {{ config.errorMessage }}
    </NotificationBox>

    <Panel v-else type="block" :title="panelTitle" hasFullscreen>
        <template v-if="isTabBarVisible" #preBody>
            <PanelNavigationBar v-model="selectedTab"
                                :items="navigationTabs" />
        </template>

        <LiveEventTab v-show="isLiveEventTab"
                      :configuration="config"
                      :occurrenceIdKey="occurrenceIdKey"
                      :realTimeTopic="realTimeTopic"
                      :initialActionIdKey="initialActionIdKey"
                      :initialVisualizerActionIdKey="initialVisualizerActionIdKey" />

        <ModerationTab v-show="isModerationTab"
                       :configuration="config"
                       :occurrenceIdKey="occurrenceIdKey"
                       :realTimeTopic="realTimeTopic" />

        <LiveQuestionsTab v-show="isLiveQuestionsTab"
                          :configuration="config"
                          :occurrenceIdKey="occurrenceIdKey"
                          :realTimeTopic="realTimeTopic" />

        <div v-show="isLiveQuestionsTab"></div>
    </Panel>
</template>

<script setup lang="ts">
    import NotificationBox from "@Obsidian/Controls/notificationBox.obs";
    import LiveEventTab from "./ExperienceManager/liveEventTab.partial.obs";
    import LiveQuestionsTab from "./ExperienceManager/liveQuestionsTab.partial.obs";
    import ModerationTab from "./ExperienceManager/moderationTab.partial.obs";
    import Panel from "@Obsidian/Controls/panel";
    import PanelNavigationBar from "./ExperienceManager/panelNavigationBar.partial.obs";
    import { useConfigurationValues, useReloadBlock, onConfigurationValuesChanged } from "@Obsidian/Utility/block";
    import { ExperienceManagerInitializationBox } from "@Obsidian/ViewModels/Blocks/Event/InteractiveExperiences/ExperienceManager/experienceManagerInitializationBox";
    import { computed, provide, ref } from "vue";
    import { ListItemBag } from "@Obsidian/ViewModels/Utility/listItemBag";
    import { getTopic, ITopic } from "@Obsidian/Utility/realTime";
    import { IParticipantTopic } from "./types.partial";

    const config = useConfigurationValues<ExperienceManagerInitializationBox>();

    // #region Values

    const availableTabs = (config.tabsToShow ?? ["Live Event", "Moderation", "Live Questions"]) as ("Live Event" | "Moderation" | "Live Questions")[];
    const isTabBarVisible = availableTabs.length > 1;
    const navigationTabs: ListItemBag[] = (availableTabs ?? []).map(s => ({ value: s, text: s }));
    const selectedTab = ref<"Live Event" | "Moderation" | "Live Questions">(availableTabs[0]);
    const realTimeTopic = ref<ITopic<IParticipantTopic> | null>(null);
    const occurrenceIdKey = ref<string | null>(null);
    const initialActionIdKey = ref<string | null>(null);
    const initialVisualizerActionIdKey = ref<string | null>(null);

    // #endregion

    // #region Computed Values

    const panelTitle = computed((): string => {
        return `${config.experienceName}`;
    });

    const isLiveEventTab = computed((): boolean => {
        return selectedTab.value === "Live Event";
    });

    const isModerationTab = computed((): boolean => {
        return selectedTab.value === "Moderation";
    });

    const isLiveQuestionsTab = computed((): boolean => {
        return selectedTab.value === "Live Questions";
    });

    // #endregion

    // #region Functions

    async function startRealTime(): Promise<void> {
        if (!config.experienceToken) {
            throw new Error("Missing experience token from server.");
        }

        var topic = await getTopic<IParticipantTopic>("Rock.RealTime.Topics.InteractiveExperienceParticipantTopic");

        topic.onDisconnected(startRealTime);

        const response = await topic.server.joinExperience(config.experienceToken);

        occurrenceIdKey.value = response.occurrenceIdKey ?? null;
        initialActionIdKey.value = response.currentActionIdKey ?? null;
        initialVisualizerActionIdKey.value = response.currentVisualizerActionIdKey ?? null;
        realTimeTopic.value = topic;
    }

    // #endregion

    // #region Event Handlers

    // #endregion

    onConfigurationValuesChanged(useReloadBlock());

    startRealTime();

    provide("ExperienceManagerRealTimeTopic", realTimeTopic);
</script>
